import { CloudOff, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BookingSummary } from '../components/BookingRow';

interface OfflineCacheState {
  bookings: BookingSummary[];
  photoCount: number;
  loaded: boolean;
}

/**
 * Branded offline page mounted at /account/offline. The service worker may
 * route here when both network + cache fail for a navigation request; users
 * may also bookmark it. We try to read whatever the SW has cached for the
 * /api/customer/* surface so the page has something useful to show.
 */
export function OfflinePage() {
  const [state, setState] = useState<OfflineCacheState>({
    bookings: [],
    photoCount: 0,
    loaded: false,
  });

  useEffect(() => {
    void readOfflineCache().then(setState);
  }, []);

  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'var(--background, oklch(0.985 0.003 85))',
        padding: '3rem 1.5rem',
      }}
    >
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '14px',
            background: 'oklch(0.96 0.02 80)',
            color: 'var(--brand-primary, var(--primary, #004AAD))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1.25rem',
          }}
          aria-hidden
        >
          <CloudOff size={24} />
        </div>
        <h1
          style={{
            fontSize: '2rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0 0 0.6rem',
          }}
        >
          You're offline
        </h1>
        <p
          style={{
            margin: 0,
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            fontSize: '0.95rem',
            lineHeight: 1.55,
          }}
        >
          Some features won't work until you reconnect. Here's what we have saved on your device.
        </p>

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '0.55rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--brand-primary, var(--primary, #004AAD))',
              color: 'white',
              fontSize: '0.88rem',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
          >
            <RefreshCw size={14} />
            {isOnline ? 'Reload' : 'Try again'}
          </button>
          <Link
            to="/account"
            style={{
              padding: '0.55rem 1rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'transparent',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
              fontSize: '0.88rem',
              fontWeight: 500,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Back to my account
          </Link>
        </div>

        <section
          style={{
            marginTop: '2rem',
            border: '1px solid var(--border, oklch(0.915 0.005 80))',
            borderRadius: '14px',
            background: 'var(--surface, white)',
            padding: '1.1rem 1.2rem',
          }}
        >
          <h2 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600 }}>
            Saved on this device
          </h2>
          {!state.loaded && (
            <p
              style={{
                margin: 0,
                fontSize: '0.85rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              Checking your saved data…
            </p>
          )}
          {state.loaded && state.bookings.length === 0 && state.photoCount === 0 && (
            <p
              style={{
                margin: 0,
                fontSize: '0.85rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              We haven't saved anything yet. Once you're back online, visit your bookings and pets
              to make them available offline next time.
            </p>
          )}
          {state.loaded && state.bookings.length > 0 && (
            <ul
              style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.5rem' }}
            >
              {state.bookings.slice(0, 8).map((b) => (
                <li
                  key={b.id}
                  style={{
                    padding: '0.55rem 0.7rem',
                    borderRadius: '8px',
                    background: 'oklch(0.98 0.004 80)',
                    fontSize: '0.85rem',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{b.type}</span>{' '}
                  <span
                    className="num"
                    style={{
                      color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                    }}
                  >
                    {b.startAt?.slice(0, 10)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {state.loaded && state.photoCount > 0 && (
            <p
              style={{
                margin: '0.75rem 0 0',
                fontSize: '0.82rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              <span className="num">{state.photoCount}</span> photo
              {state.photoCount === 1 ? '' : 's'} cached for offline viewing.
            </p>
          )}
        </section>
      </div>
    </main>
  );
}

async function readOfflineCache(): Promise<OfflineCacheState> {
  const empty: OfflineCacheState = { bookings: [], photoCount: 0, loaded: true };
  if (typeof caches === 'undefined') return empty;
  try {
    const cache = await caches.open('kennel-api-v1');
    const reqs = await cache.keys();
    let bookings: BookingSummary[] = [];
    let photoCount = 0;
    for (const req of reqs) {
      const url = new URL(req.url);
      if (
        url.pathname === '/api/customer/bookings' ||
        url.pathname.startsWith('/api/customer/bookings?')
      ) {
        const res = await cache.match(req);
        if (!res) continue;
        try {
          const body = (await res.clone().json()) as {
            bookings?: BookingSummary[];
          };
          if (Array.isArray(body.bookings)) bookings = body.bookings;
        } catch {
          // ignore parse errors
        }
      }
      if (url.pathname.includes('/photos')) photoCount += 1;
    }
    return { bookings, photoCount, loaded: true };
  } catch {
    return empty;
  }
}
